import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import * as _ from 'lodash-es';

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, flatMap, map, switchMap, take, tap } from 'rxjs/operators';

import { ISubjectList, Subject, SubjectMarker, SubjectTemplate, SubjectUserSecurity } from '../models/subject';
import { SubjectType } from '../models/subject-type';
import { BaseRestService } from './base-rest.service';
import { SubjectTypeService } from './subject-type.service';
import { UserService } from './user.service';
import { User } from '../models/user';
import { StateService } from './state.service';
import { SearchService } from './search.service';
import { MatDialog } from '@angular/material/dialog';
import { SubjectMatchComponent } from 'app/main/content/subjects/subject-match/subject-match.component';
import { MessageService } from './messages/message.service';
import { HydrateSubjectService } from './hydrate-subject.service';
import { EventService } from './event.service';
import { SubjectLink } from '../models/subject-link';
import { OrganisationService } from './organisation.service';
import { StatusService } from './status.service';
import { UserSubjectHistoryService } from './user-subject-history.service';
import { RULER_CLASS_NAME } from 'devexpress-richedit/lib/base/ui/ruler/settings';
import { UserSecurityDto } from '../models/user-security-dto';
// import { ProcessType } from '../models/process-type';
// import { Process } from '../models/process';
// import { Status } from '../models/status';
// import { ProcessService } from './process.service';

export enum enumSubjectFilter {
  AllSubjects = 0,
  MySubjects = 1,
  UserSubjects = 2,
  ManagedSubjects = 3,
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({ providedIn: 'root' })
export class SubjectService extends BaseRestService<Subject, ISubjectList> {
  subjectFilter: enumSubjectFilter = enumSubjectFilter.AllSubjects;
  organisationFilter: string;
  stateFilter: string;
  statusFilter: string;
  categoryFilter: string;
  caseTypeFilter: string;
  reviewDaysFilter: string;
  usersFilter: string;
  archivedFilter: boolean;
  dueForDeletionFilter: boolean;
  referralFilter: boolean;
  myReferralFilter: boolean;

  markerSortAscending = true;

  constructor(
    http: HttpClient,
    private subjectTypeService: SubjectTypeService,
    private userService: UserService,
    private stateService: StateService,
    // private statusService: StatusService,
    private searchService: SearchService,
    private messageService: MessageService,
    private hydrateSubjectService: HydrateSubjectService,
    private organisationService: OrganisationService,
    private eventService: EventService,
    private userSubjectHistoryService: UserSubjectHistoryService,
    // private processTypeService: ProcessTypeService,
    // private processService:ProcessService,
    protected router: Router,
    public dialog: MatDialog
  ) {
    super(SubjectService, http, 'api/subjects', 'subject', router);
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // tslint:disable-next-line:no-inferrable-types
    let subjectTypeName: string = '';
    this.routeParams = route.params;
    this.stateFilter = '';
    this.statusFilter = '';
    this.usersFilter = '';
    this.caseTypeFilter = '';
    this.categoryFilter = '';
    this.organisationFilter = '';
    this.reviewDaysFilter = '0';
    this.subjectFilter = enumSubjectFilter.AllSubjects;

    if (this.routeParams.subjecttype) {
      subjectTypeName = this.routeParams.subjecttype;
    }
    if (this.routeParams.id) {
      if (
        !this.routeParams.subjecttype &&
        !(this.routeParams.id === 'new' || /\d/.test(this.routeParams.id))
      ) {
        subjectTypeName = this.routeParams.id;
      }
    }
    if (this.routeParams.state) {
      const name = this.routeParams.state.toLowerCase();
      const subjectState = this.stateService.cache.find(
        (i) => i.name.toLowerCase() === name
      );
      this.stateFilter = subjectState ? subjectState.uid : '';
    }
    if (this.routeParams.user) {
      const user: string = this.routeParams.user.toLowerCase();
      const ignore = ['all', 'managers', 'my', 'users'];
      if (!ignore.includes(user)) {
        this.usersFilter = user;
      }
    }
    if (this.routeParams.status) {
      this.statusFilter = this.routeParams.status;
    }

    if (this.routeParams.category) {
      this.categoryFilter = this.routeParams.category;
    }

    if (this.routeParams.caseType) {
      this.caseTypeFilter = this.routeParams.caseType;
    }

    if (this.routeParams.reviewDays) {
      this.reviewDaysFilter = this.routeParams.reviewDays;
    }

    if (this.routeParams.organisation) {
      const name = this.routeParams.organisation.toLowerCase();
      const subjectOrganisation = this.organisationService.cache.find(
        (i) => i.name.toLowerCase() === name
      );
      this.organisationFilter = subjectOrganisation
        ? subjectOrganisation.uid
        : '';
    }

    const s = route.url.toString().toLowerCase() + ' ';

    if (s.indexOf(',all ') > -1 || s.indexOf(',all,') > -1) {
      this.subjectFilter = enumSubjectFilter.AllSubjects;
    }
    if (s.indexOf(',users ') > -1 || s.indexOf(',users,') > -1) {
      this.subjectFilter = enumSubjectFilter.UserSubjects;
    }
    if (s.indexOf(',managers ') > -1 || s.indexOf(',managers,') > -1) {
      this.subjectFilter = enumSubjectFilter.ManagedSubjects;
    }
    if (s.indexOf(',my ') > -1 || s.indexOf(',my,') > -1) {
      this.subjectFilter = enumSubjectFilter.MySubjects;
    }

    this.archivedFilter =
      s.indexOf(',archived ') > -1 || s.indexOf(',archived,') > -1;
    this.dueForDeletionFilter =
      s.indexOf(',duefordeletion ') > -1 || s.indexOf(',duefordeletion,') > -1;
    this.referralFilter =
      s.indexOf('referrals') > -1 && !(s.indexOf(',u,my') > -1);
    this.myReferralFilter =
      (s.indexOf(',my ') > -1 || s.indexOf(',my,') > -1) &&
      s.indexOf('referrals') > -1;

    if (this.referralFilter && !subjectTypeName) {
      // MRD - referral type is currently hard coded to case...
      const subjectType = this.subjectTypeService.getByIdFromCache(1);
      subjectTypeName = subjectType ? subjectType.name : 'case';
    }

    if (this.myReferralFilter) {
      subjectTypeName = 'case';
    }

    return super.resolve(route, state, subjectTypeName);
  }

  getAll(type?: string, urlParams?: string) {
    urlParams = this.addUrlParam(urlParams, 'include', 'links');
    if (
      (this.subjectFilter === enumSubjectFilter.MySubjects ||
        this.myReferralFilter) &&
      !this.archivedFilter &&
      !this.dueForDeletionFilter
    ) {
      urlParams = this.addUrlParam(
        urlParams,
        'assigned',
        this.userService.getCurrentUserUid()
      );
    }
    if (this.organisationFilter) {
      urlParams = this.addUrlParam(
        urlParams,
        'organisation',
        this.organisationFilter
      );
    }
    if (this.usersFilter) {
      urlParams = this.addUrlParam(urlParams, 'users', this.usersFilter);
    }
    if (this.stateFilter) {
      urlParams = this.addUrlParam(urlParams, 'state', this.stateFilter);
    }
    if (this.statusFilter) {
      urlParams = this.addUrlParam(urlParams, 'status', this.statusFilter);
    }
    if (this.categoryFilter) {
      urlParams = this.addUrlParam(urlParams, 'category', this.categoryFilter);
    }
    if (this.caseTypeFilter) {
      urlParams = this.addUrlParam(urlParams, 'caseType', this.caseTypeFilter);
    }

    if (this.archivedFilter) {
      urlParams = this.addUrlParam(urlParams, 'archived', 'true');
    }
    if (this.dueForDeletionFilter) {
      urlParams = this.addUrlParam(urlParams, 'duefordeletion', 'true');
    }
    if (this.referralFilter || this.myReferralFilter) {
      urlParams = this.addUrlParam(urlParams, 'referral', 'true');
    }

    if (this.reviewDaysFilter) {
      urlParams = this.addUrlParam(
        urlParams,
        'reviewDays',
        this.reviewDaysFilter
      );
    }

    // if (this.myReferralFilter) {
    //   urlParams = this.addUrlParam(urlParams, 'myreferrals', 'true');
    //   console.log(urlParams);
    // }
    super.getAll(type, urlParams);
  }

  applyDataFilter(data: Subject[]): Subject[] {
    const selectedUsers = this.userService.selectedUsers || [];
    switch (this.subjectFilter) {
      case enumSubjectFilter.MySubjects:
        return data.filter(
          (i) =>
            Array.isArray(i.subjectUsers) &&
            i.subjectUsers.length &&
            this.userService.getCurrentUserId() === i.subjectUsers[0].userId
        );
      case enumSubjectFilter.UserSubjects:
        return data.filter(
          (i) =>
            Array.isArray(i.subjectUsers) &&
            i.subjectUsers.length &&
            selectedUsers.indexOf(i.subjectUsers[0].userId) !== -1
        );
      case enumSubjectFilter.ManagedSubjects:
        return data.filter(
          (i) =>
            Array.isArray(i.subjectUsers) &&
            i.subjectUsers.length &&
            selectedUsers.indexOf(i.subjectUsers[0].managerId) !== -1
        );
      default:
        return data;
    }
  }

  public getSubjectTypeName(subjectType: string | SubjectType): string {
    subjectType = subjectType || '';
    return typeof subjectType === 'string' ? subjectType : subjectType.name;
  }

  // for use where it is necessary to lookup subjects based on subject type
  getAllBySubjectType(
    subjectType: string | SubjectType,
    includeReferrals?: boolean
  ): Observable<Subject[]> {
    let urlParams = '';
    if (includeReferrals) {
      urlParams = this.addUrlParam(urlParams, 'includeReferrals', 'true');
    }
    return this.getAllData(this.getSubjectTypeName(subjectType), urlParams);
  }

  getSubjectType(subject: Subject) {
    return this.subjectTypeService.getById(subject.subjectTypeId);
  }

  saveLayout(subject: Subject, message?: string): Observable<any> {
    if (
      subject &&
      subject.subjectTemplates &&
      subject.subjectTemplates.length > 0
    ) {
      const theUrl = `${this.resourceUrl}/${subject.uid}/saveLayout`;
      const template = subject.subjectTemplates.filter(
        (subjectTemplate) =>
          subjectTemplate.template.userId ===
          this.userService.getCurrentUserId()
      );
      return this.http.put(theUrl, template[0]).pipe(
        tap((response: SubjectTemplate) => {
          if (response) {
            subject.subjectTemplates.splice(0, 1, response);
            if (message) {
              this.messageService.success(message);
            }
          }
        }),
        catchError(
          this.handleError<boolean>(`Save layout for ${this.CacheName}`, true)
        )
      );
    }
  }

  setUserSecurity(
    subject: Subject,
    users: number[] | User[],
    userSecurityDto: UserSecurityDto[],
    url?: string
  ): Observable<boolean> {
    const theUrl = url || `${this.resourceUrl}/${subject.uid}/setUserSecurity`;
    let userIds = '-1';
    if (users && users.length > 0) {
      if (typeof users[0] === 'number') {
        userIds = users.join(',');
      } else {
        userIds = (users as User[]).map((i) => i.id).join(',');
      }
    }
    return this.http.post<boolean>(theUrl, userSecurityDto).pipe(
      tap((response) => {
        this.messageService.success(
          response === true
            ? 'Security restrictions updated'
            : 'Security restrictions removed'
        );
      }),
      catchError(
        this.handleError<boolean>(
          `Applying Security to ${this.CacheName}`,
          true
        )
      )
    );
  }

  getUserSecurity(subject: Subject, url?: string): Observable<number[]> {
    if (!subject.uid) {
      return of([]);
    }
    const theUrl = url || `${this.resourceUrl}/${subject.uid}/getUserSecurity`;
    return this.http.get<string>(theUrl).pipe(
      take(1),
      map((result) => {
        return result && result.length > 0
          ? result.split(',').map((i) => parseInt(i, 10))
          : [];
      })
    );
  }

  getSubjectUserSecurity(subject: Subject, userId: number, url?: string): Observable<SubjectUserSecurity> {
    if (!subject?.uid) {
      return of();
    }
    const theUrl = url || `${this.resourceUrl}/${subject.uid}/getSubjectUserSecurity/${userId}`;
    return this.http.get<SubjectUserSecurity>(theUrl).pipe(
      take(1),
      map((result) => {
        return result;
      })
    );
  }

  getSubjectUserSecurityList(subject: Subject, url?: string): Observable<SubjectUserSecurity[]> {
    if (!subject?.uid) {
      return of([]);
    }
    const theUrl = url || `${this.resourceUrl}/${subject.uid}/getSubjectUserSecurityList`;
    return this.http.get<SubjectUserSecurity[]>(theUrl).pipe(
      take(1),
      map((result) => {
        return result;
      })
    );
  }

  addLink(entity: SubjectLink) {
    return this.http
      .post<SubjectLink>('/api/subjectlinks', entity, httpOptions)
      .pipe(
        catchError(this.handleError<SubjectLink>(`Add ${this.CacheName}`, true))
      );
  }

  getSubjectWithLinks(subject: Subject): Observable<Subject> {
    return subject && subject.uid
      ? this.getSubject(subject.uid, 'links')
      : of(subject);
  }

  getFullSubject(uid: string): Observable<Subject> {
    return this.getSubject(uid, 'full');
  }

  // get subject with links, data and template information
  public getSubject(
    uid: string,
    include?: string,
    url?: string,
    operation?: string
  ): Observable<Subject> {
    if (uid) {
      const getUrl = `${url || this.resourceUrl}/${uid}/${include || 'full'}`;
      return this.cacheable(
        getUrl,
        this.http.get<any>(getUrl).pipe(
          map((response) => {
            const subject = response;
            if (subject) {
              return this.hydrate(subject).pipe(
                take(1),
                map((hydratedSubject) => {
                  return hydratedSubject;
                })
              );
            } else {
              return of(null);
            }
          }),
          catchError(
            this.handleError(
              operation || `Get full Subject from ${this.CacheName}`,
              true,
              []
            )
          )
        ),
        3000
      );
    }
  }

  delete(
    entity: Subject | string | number,
    resetCache?: boolean
  ): Observable<Subject> {
    if (typeof entity !== 'string' && typeof entity !== 'number') {
      var ss: Subject = new Subject(entity);
      if (ss && ss.isReferral) this.eventService.updateReferralStats();
      else this.eventService.updateSubjectStats();
    }

    this.eventService.updateSubjectsDueReviewStats();
    return super.delete(entity, resetCache).pipe(
      map((response) => {
        if (typeof entity !== 'string' && typeof entity !== 'number') {
          this.eventService.deleteSubject(entity);
        }
        this.userSubjectHistoryService.forceRefresh();

        return response;
      })
    );
  }

  hydrate(item: Subject): Observable<Subject> {
    return this.hydrateSubjectService.hydrate(item);
  }

  // MRD - lookup objects, such as markers, users etc need to be stripped so we don't get issues with EF tracking conflicts on save
  public dehydrate(subject: Subject): Subject {
    return this.hydrateSubjectService.dehydrate(subject);
  }

  save(
    entity: Subject,
    successMessage?: string,
    checkforExisting: boolean = true
  ) {
    const entityId = entity.id;
    const restricted = entity.restricted;
    const categoryId = entity.categoryId;

    this.saveAsObservable(entity, successMessage, checkforExisting).subscribe(
      (response) => {
        if (
          response &&
          response.restricted !== restricted &&
          response.category &&
          response.category.autoRestrict
        ) {
          // subject security has been changed on the save, potentially by a change in category.  Notify listeners just in case
          this.getFullSubject(response.uid)
            .pipe(take(1))
            .subscribe((subject) => {
              if (!subject) {
                // if get full subject doesn't return a subject then it either no longer exists or the current user does not have permission to view it
                // either way we need to notify listeners that the subject should be removed
                this.eventService.deleteSubject(response);
                if (!entityId) {
                  this.afterSave(response);
                  this.onAfterSave.next(response);
                }
              } else {
                subject.restricted = response.restricted;
                this.afterSave(subject);
                this.onAfterSave.next(subject);
              }
            });
        } else {
          this.afterSave(response);
          this.onAfterSave.next(response);
        }
      }
    );
  }

  afterSave(entity) {
    this.userSubjectHistoryService.forceRefresh();
  }

  copy(entity: Subject) {
    if (entity) {
      const clone = _.cloneDeep(entity);

      clone.linkedSubjectMarkers = undefined;
      if (clone.subjectMarkers && clone.subjectMarkers.length > 0) {
        clone.subjectMarkers.map((i) => {
          if (i.markerId) {
            i.marker = undefined;
          }
          if (i.subjectId) {
            (i as any).subject = undefined;
          }
          return i;
        });
      }
      if (clone.subjectLinks && clone.subjectLinks.length > 0) {
        clone.subjectLinks.map((i) => {
          if (i.linkedSubject && i.linkedSubjectId) {
            i.linkedSubject = undefined;
          }
          if (i.subject && i.subjectId) {
            i.subject = undefined;
          }
          return i;
        });
      }
      return clone;
    } else {
      return entity;
    }
  }

  saveAsObservable(
    entity: Subject,
    successMessage?: string,
    checkforExisting: boolean = true,
    disableSecurityCheck: boolean = false
  ): Observable<Subject> {
    // make a copy of the subject to be saved - we do this as if we 'unset' related objects on the entity itself it will affect the passed entity
    const subject = this.copy(entity);

    // if is an existing subject, or checkforexisting is set to false then attempt save
    if (entity.id || !checkforExisting) {
      return this.doSave(subject, successMessage, disableSecurityCheck);
    }

    // if got here then check for existing matching subject(s)
    return this.checkForMatches(subject).pipe(
      take(1),
      switchMap((d) => {
        if (d && d.cancelled === true) {
          return of(null);
        }
        if (d && d.existingSubject) {
          return of(d.existingSubject);
        } else {
          // add as new subject
          if (d && d.data) {
            let uniqueRef =
              d.data.existingSubject &&
              subject.reference === d.data.existingSubject.reference;
            if (!uniqueRef && d.data.matchingSubjects) {
              d.data.matchingSubjects.forEach((x) => {
                uniqueRef = uniqueRef || subject.reference === x.reference;
              });
            }
            // TODO:
          }
          return this.doSave(subject, successMessage, disableSecurityCheck);
        }
      })
    );
  }

  private checkForMatches(subject): Observable<any> {
    if (
      subject &&
      subject.subjectType &&
      subject.subjectType.disableSubjectMatching
    ) {
      // if disableSubjectMatching is set then we can bypass the search and just return the new subject
      return of({ subject });
    } else {
      return this.searchService
        .getSearchData('', subject.summary, subject.subjectTypeId)
        .pipe(
          take(1),
          switchMap((matchingSubjects: Subject[]) => {
            if (matchingSubjects && matchingSubjects.length > 0) {
              const data =
                matchingSubjects.length === 1
                  ? { subject, existingSubject: matchingSubjects[0] }
                  : { subject, matchingSubjects };
              const dlg = this.dialog.open(SubjectMatchComponent, { data });
              return dlg.afterClosed();
            } else {
              return of({ subject });
            }
          }),
          switchMap((result) => {
            return of(result);
          })
        );
    }
  }

  private doSave(
    subject: Subject,
    successMessage?: string,
    disableSecurityCheck: boolean = false
  ): Observable<Subject> {
    this.dehydrate(subject);

    if (disableSecurityCheck === true) {
      return this.internalDoSave(subject, successMessage);
    } else {
      return this.checkSecurity(subject).pipe(
        map((rc) => {
          if (rc.length > 0) {
            this.messageService.error(rc);
            return 0;
          }
          return 1;
        }),
        flatMap((x) => {
          if (x === 1) {
            return this.internalDoSave(subject, successMessage);
          } else return of(null);
        })
      );
    }
  }

  private internalDoSave(
    subject: Subject,
    successMessage: string
  ): Observable<Subject> {
    if (subject.isReferral) this.eventService.updateReferralStats();
    else this.eventService.updateSubjectStats();

    const currentRef = subject.reference;
    const save$ = subject.id
      ? this.put(subject, successMessage)
      : this.post(subject, successMessage);
    return save$.pipe(
      map((s) => {
        if (successMessage && currentRef && s) {
          const newRef = s.reference;
          if (newRef !== currentRef && newRef.includes(currentRef)) {
            this.messageService.info(
              `The reference '${currentRef}' is not unique and has been amended to '${newRef}'`
            );
          }
        }
        this.userSubjectHistoryService.forceRefresh();
        return s;
      })
    );
  }

  checkSecurity(subject: Subject): Observable<string> {
    return this.getUserSecurity(subject).pipe(
      take(1),
      map((AllowedUsers) => {
        let rc = '';
        if (
          AllowedUsers &&
          AllowedUsers.length > 0 &&
          (subject && subject.subjectUsers && subject.subjectUsers.length | 0) >
          0
        ) {
          subject.subjectUsers.forEach((su) => {
            if (rc === '') {
              if (
                su.managerId &&
                su.managerId > 0 &&
                AllowedUsers.findIndex((i) => i === su.managerId) === -1
              ) {
                const user = this.userService.getUserById(su.managerId);
                rc = `The specified manager '${user.firstName + ' ' + user.lastName
                  }' is not allowed because of the subject's security settings`;
              }
              if (
                rc.length === 0 &&
                su.userId &&
                su.userId > 0 &&
                AllowedUsers.findIndex((i) => i === su.userId) === -1
              ) {
                const user = this.userService.getUserById(su.userId);
                rc = `The specified user '${user.firstName + ' ' + user.lastName
                  }' is not allowed because of the subject's security settings`;
              }
            }
          });
        }
        return rc;
      })
    );
  }

  batchReassignSubjects(
    selectedSubjects: any[],
    selectedUserId: any
  ): Observable<string> {
    const theUrl = `${this.resourceUrl}/batchReassignSubjects`;
    return this.http.post<string>(theUrl, { selectedSubjects, selectedUserId });
  }

  getStatusIdFromString(str: string): number {
    if (!str) return -1;
    const match = str.match(/\d+$/);
    const number = match ? parseInt(match[0]) : NaN;
    return number;
  }

  canSaveSubject(subject: Subject): Observable<boolean> {
    const theUrl = `${this.resourceUrl}/canSaveSubject`;

    // We need to clone the subject so that we can  set assessments to null and remove any circular references
    var subjectClone: Subject = new Subject(subject);
    subjectClone.subjectAssessments = null;
    var sub = BaseRestService.RemoveCircularReferences(subjectClone);

    // Make the call to the server
    return this.http.put<boolean>(theUrl, sub).pipe(take(1));
  }
}